<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import Sortable from 'sortablejs';

const props = defineProps<{
  /** Список страниц — Blob (JPEG/PNG) или File (для PDF из share-target / Lens) */
  pages: Array<Blob | File>;
  /** Включён ли drag-reorder. По умолчанию да. */
  reorder?: boolean;
}>();

const emit = defineEmits<{
  /** Изменили порядок — новый список */
  reordered: [pages: Array<Blob | File>];
  /** Переснять страницу с индексом — App.vue откроет камеру в режиме retake */
  retake: [index: number];
  /** Удалить страницу с индексом */
  remove: [index: number];
}>();

const listEl = ref<HTMLDivElement | null>(null);
let sortable: Sortable | null = null;

// URL.createObjectURL для preview thumbnails. Кешируем по идентичности Blob.
const blobUrls = ref<Map<Blob | File, string>>(new Map());

function urlFor(blob: Blob | File): string {
  let url = blobUrls.value.get(blob);
  if (!url) {
    url = URL.createObjectURL(blob);
    blobUrls.value.set(blob, url);
  }
  return url;
}

const thumbs = computed(() =>
  props.pages.map((blob, index) => {
    const isPdf =
      ('type' in blob && blob.type === 'application/pdf') ||
      ('name' in blob && (blob as File).name?.toLowerCase().endsWith('.pdf'));
    return {
      index,
      blob,
      url: isPdf ? null : urlFor(blob),  // PDF не превью-ить без pdf.js — просто иконка
      isPdf,
      name: 'name' in blob ? (blob as File).name : `Страница ${index + 1}`,
      size: blob.size,
    };
  })
);

onMounted(() => {
  if (props.reorder !== false && listEl.value) {
    sortable = Sortable.create(listEl.value, {
      animation: 180,
      delay: 150,            // long-press 150ms чтобы случайно не двигать
      delayOnTouchOnly: true, // на десктопе drag сразу, на телефоне с задержкой
      ghostClass: 'thumb-ghost',
      chosenClass: 'thumb-chosen',
      onEnd: (evt) => {
        if (evt.oldIndex === undefined || evt.newIndex === undefined) return;
        if (evt.oldIndex === evt.newIndex) return;
        // Создаём новый массив с переставленными элементами и эмитим наружу
        const newPages = [...props.pages];
        const [moved] = newPages.splice(evt.oldIndex, 1);
        newPages.splice(evt.newIndex, 0, moved);
        emit('reordered', newPages);
        // Haptic
        try {
          navigator.vibrate?.(20);
        } catch {
          /* ignore */
        }
      },
    });
  }
});

onUnmounted(() => {
  if (sortable) {
    sortable.destroy();
    sortable = null;
  }
  // Освобождаем object URLs
  for (const url of blobUrls.value.values()) {
    URL.revokeObjectURL(url);
  }
  blobUrls.value.clear();
});

// Если pages обновился извне (например после auto-capture) — очищаем URL'ы
// которых больше нет
watch(
  () => props.pages,
  (newPages) => {
    const currentBlobs = new Set(newPages);
    for (const [blob, url] of blobUrls.value.entries()) {
      if (!currentBlobs.has(blob)) {
        URL.revokeObjectURL(url);
        blobUrls.value.delete(blob);
      }
    }
  },
  { deep: false },
);

</script>

<template>
  <div class="thumb-strip">
    <div class="thumb-strip-header">
      <span class="thumb-count">Страниц: {{ pages.length }}</span>
      <span v-if="reorder !== false && pages.length > 1" class="thumb-hint">
        Удерживайте для перестановки
      </span>
    </div>
    <div ref="listEl" class="thumb-list">
      <div
        v-for="t in thumbs"
        :key="`page-${t.index}-${t.size}`"
        class="thumb-item"
      >
        <div class="thumb-image">
          <img v-if="t.url" :src="t.url" :alt="t.name" loading="lazy" />
          <div v-else class="thumb-pdf">
            <span class="thumb-pdf-icon">📄</span>
            <span class="thumb-pdf-label">PDF</span>
          </div>
          <span class="thumb-number">{{ t.index + 1 }}</span>
        </div>
        <div class="thumb-actions">
          <button
            type="button"
            class="thumb-btn retake"
            :aria-label="`Переснять страницу ${t.index + 1}`"
            @click="emit('retake', t.index)"
          >
            ↻
          </button>
          <button
            type="button"
            class="thumb-btn remove"
            :aria-label="`Удалить страницу ${t.index + 1}`"
            @click="emit('remove', t.index)"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.thumb-strip {
  width: 100%;
  background: #f1f3f5;
  border-radius: 12px;
  padding: 12px;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.04);
}
.thumb-strip-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.85rem;
  margin-bottom: 8px;
  color: var(--max-hint-color, #555);
}
.thumb-count {
  font-weight: 600;
}
.thumb-hint {
  font-size: 0.75rem;
  opacity: 0.7;
}
.thumb-list {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
}
.thumb-item {
  flex: 0 0 auto;
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  gap: 4px;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  cursor: grab;
}
.thumb-item:active {
  cursor: grabbing;
}
.thumb-image {
  position: relative;
  width: 84px;
  height: 112px;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
  border: 2px solid transparent;
  transition: border-color 120ms ease;
}
.thumb-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  pointer-events: none;
}
.thumb-pdf {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fff5ee;
  color: #d97706;
  gap: 2px;
}
.thumb-pdf-icon {
  font-size: 2rem;
  line-height: 1;
}
.thumb-pdf-label {
  font-size: 0.7rem;
  font-weight: 700;
}
.thumb-number {
  position: absolute;
  top: 4px;
  left: 4px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 999px;
  line-height: 1;
}
.thumb-actions {
  display: flex;
  gap: 4px;
  justify-content: space-between;
}
.thumb-btn {
  flex: 1;
  min-width: 36px;
  min-height: 36px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 120ms ease, transform 100ms ease;
}
.thumb-btn:active {
  transform: scale(0.92);
}
.thumb-btn.retake {
  background: rgba(37, 99, 235, 0.15);
  color: #2563eb;
}
.thumb-btn.retake:hover {
  background: rgba(37, 99, 235, 0.25);
}
.thumb-btn.remove {
  background: rgba(220, 38, 38, 0.12);
  color: #dc2626;
}
.thumb-btn.remove:hover {
  background: rgba(220, 38, 38, 0.2);
}

/* Sortable.js drag states */
.thumb-ghost {
  opacity: 0.4;
}
.thumb-chosen .thumb-image {
  border-color: #2563eb;
  transform: scale(1.04);
  box-shadow: 0 4px 16px rgba(37, 99, 235, 0.35);
}
</style>
